/* eslint-disable no-unused-vars */
import React, { FC } from 'react';
import { Box, Heading, Image, Link, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import NextLink from 'next/link';
import Page from '../components/Page';

const About: FC = () => {
  return (
    <Page
      seoOptions={{
        title: 'About SmartParse: The Team Behind Your Data Integration',
        description:
          'Meet the team behind SmartParse, where developers build solutions for developers. We focus on making data ' +
          'integration simple and reliable, because we understand your challenges.',
        canonical: 'https://smartparse.io/about/'
      }}
      maxW='100%'
      p={0}
      px={4}
      m={0}
      mx={4}
    >
      <VStack maxW='4xl' mx='auto' px={8} py={8} align='start' spacing={2}>
        <Heading as='h1' size={{ base: 'md', md: 'xl' }} mb={8}>
          About SmartParse: The Team Behind Your Data Integration
        </Heading>

        <Text>
          In the gleaming tech hubs of Cape Town, where startups bloom like fynbos after rain, we found ourselves
          pondering a peculiarly modern problem. Why, in an age where robots can write poetry and cars can drive
          themselves, were businesses still struggling with the mundane task of moving data from one place to another?
          The answer, we discovered, wasn&apos;t in creating more complexity – it was in embracing simplicity.
        </Text>

        <Text>
          SmartParse emerged in 2024 from the convergence of frustration and possibility. While others in the industry
          were building elaborate cathedrals of code, we chose to create something different: a platform that transforms
          CSV files into APIs with the kind of elegance that makes engineers smile and business owners breathe easier.
        </Text>

        <Heading as='h2' size='sm' mt={8} mb={4}>
          The People Behind the Platform
        </Heading>

        <Text>
          Our story begins with two members who&apos;ve spent years in the depths of data architecture, emerging with a
          solution that challenges the status quo.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <VStack spacing={4}>
            <Image src='/images/about/dirk.jpg' alt='Dirk Viljoen' borderRadius='lg' />
            <Text>
              <strong>Dirk Viljoen</strong>, our co-founder, brings infectious energy and deep cloud expertise to the
              team. Known for turning complex challenges into engaging conversations, Dirk has built his reputation on
              making the difficult approachable. His experience in cloud infrastructure comes with a human touch –
              he&apos;s a rare technologist who bridges the gap between technical knowledge and business needs.
            </Text>
          </VStack>

          <VStack spacing={4}>
            <Image src='/images/about/ivan.jpg' alt='Ivan Breet' borderRadius='lg' />
            <Text>
              <strong>Ivan Breet</strong>, our co-founder brings fifteen years of hard-won experience in architecting
              cloud systems and taming particularly unruly datasets. Technology developed at Simply Anvil, became the
              quiet backbone for numerous enterprises and ambitious startups, building the kind of robust infrastructure
              that powers success without drawing attention to itself.
            </Text>
          </VStack>
        </SimpleGrid>

        <Heading as='h2' size='sm' mt={8} mb={4}>
          The Technical Reality
        </Heading>

        <Text>
          Behind our simple interface lies a carefully crafted infrastructure built with security in mind. Security
          isn&apos;t just a checkbox for us – it&apos;s woven into the fabric of everything we build. Our platform
          employs industry-standard encryption and robust security practices to ensure your data remains protected. We
          believe in being transparent about our security measures while continuously improving them.
        </Text>
        <Text>
          The impact of our work isn&apos;t measured in vanity metrics or certification badges. Instead, it&apos;s
          reflected in the day-to-day experiences of developers and business owners who can finally focus on building
          their products rather than wrestling with data integration challenges. When we hear from clients about how
          they&apos;ve reclaimed their time and reduced their technical debt, that&apos;s when we know we&apos;re
          succeeding.
        </Text>

        <Heading as='h2' size='sm' mt={8} mb={4}>
          The Future, Grounded in Reality
        </Heading>

        <Text>
          Looking ahead, our roadmap is simple and focused: we evolve our product based on what our customers actually
          need. We believe in building features that solve real problems, not chasing trends. Our development priorities
          are shaped by direct feedback from the people who use SmartParse every day.
        </Text>

        <Heading as='h2' size='sm' mt={8} mb={4}>
          A Different Kind of Tech Company
        </Heading>

        <Text>
          Under the bright African sun, where innovation flourishes in unexpected places, we&apos;re proud to call South
          Africa home. There&apos;s something about this continent&apos;s spirit – its ability to find elegant solutions
          to complex challenges – that shapes everything we do. Like the clever weaver birds that craft their intricate
          nests from simple materials, we believe in building technology that&apos;s both sophisticated and practical.
          What matters to us is how we serve our customers and the quality of what we build, bringing that distinctly
          African ingenuity to every problem we solve.
        </Text>

        <Heading as='h2' size='sm' mt={8} mb={4}>
          Connect With Real Humans
        </Heading>

        <Text>
          Whether you&apos;re a developer looking to discuss the finer points of API design, or a business owner tired
          of data migration headaches, we&apos;re here to help. Our support team actually understands both technology
          and human language, and we pride ourselves on responses that don&apos;t sound like they were generated by a
          chatbot.
        </Text>

        <Text mt={20}>
          <strong>Business Inquiries and Technical Support:</strong>{' '}
          <Link as={NextLink} href='mailto:support@smartparse.io'>
            support@smartparse.io
          </Link>
        </Text>

        <Box>
          <Text>
            <strong>Location:</strong> Shop no. 12, Sloane Square
          </Text>
          <Text>18 Sloane Street, Bryanston</Text>
          <Text>Johannesburg</Text>
          <Text>Gauteng</Text>
          <Text>2191</Text>
          <Text>South Africa</Text>
        </Box>

        <Text>
          SmartParse is a division of{' '}
          <Link as={NextLink} href='https://simplyanvil.com'>
            Simply Anvil (Pty) Ltd
          </Link>
          , registered with the Companies and Intellectual Property Commission (CIPC) of South Africa: 2018/018045/07
        </Text>

        <Text fontWeight='bold' mt={20}>
          In an industry that often mistakes complexity for sophistication, we&apos;ve chosen a different path. At
          SmartParse, we believe the future of data integration lies not in adding more layers of abstraction, but in
          stripping away everything that isn&apos;t essential. It&apos;s a philosophy that&apos;s served us well so far,
          and we&apos;re just getting started.
        </Text>
      </VStack>
    </Page>
  );
};

export default About;
